import { MouseEvent, useMemo, useState } from "react"
import { Box, Checkbox, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
    faComment,
    faEllipsisV,
    faList,
    faLock,
    faLockOpen,
    faPenToSquare,
    faReceipt,
    faTrash,
} from "@fortawesome/pro-light-svg-icons"
import type { QueryClient } from "@tanstack/react-query"
import { makeStyles } from "tss-react/mui"

import { useApiMutation } from "../../../../../../../core/api/useApiMutation"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { StandardDialog } from "../../../../../../../shared/components/dialogs/StandardDialog"
import { OrderChatIcon } from "../../../../../../../shared/components/orderChat/OrderChatIcon"
import { changeLockStatusMerchant, MerchantMeetingServiceOrdersQuery } from "../../meetingServiceApi"
import { FilteredStatuses, MeetingServiceOrder, MeetingServiceOrdersView } from "../../meetingServiceTypes"
import { getHiddenOrderStatuses } from "../../meetingServiceLib"
import { MeetingTimeLeftTag } from "../../../../../../../shared/components/meetingTimeLeftTag/MeetingTimeLeftTag"
import { TimeToStart } from "../helpers"
import { MinimizedStatusButton, StatusButton } from "../../components/StatusMenu"
import { StatusChangeModal } from "../../components/StatusChangeModal"
import { MeetingServiceOrdersDependencies } from "../../components/OrderDeliveryList"
import { ProductionCardMenu, ProductionCardMenuItem } from "./ProductionCardMenu"
import { ProductionClampedName } from "./ProductionClampedName"
import { ProductionOrderCardMetaRow } from "./ProductionOrderCardMetaRow"

const useStyles = makeStyles()((theme) => ({
    headerRow: {
        boxSizing: "border-box",
        minHeight: "4.5rem",
        padding: ".75rem 1rem",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
        [theme.breakpoints.down("lg")]: {
            flexWrap: "wrap",
            rowGap: ".5rem",
        },
    },
    headerLeft: {
        minWidth: 0,
        [theme.breakpoints.down("lg")]: {
            flexBasis: "100%",
        },
    },
    headerActions: {
        marginLeft: "auto",
    },
    selectCheckbox: {
        width: "2.375rem",
        height: "2.375rem",
    },
    titleBlock: {
        minWidth: 0,
        rowGap: ".375rem",
        [theme.breakpoints.down("lg")]: {
            flexWrap: "wrap",
        },
    },
    title: {
        fontWeight: 600,
        color: theme.palette.tailwind.slate[800],
    },
    actionsDivider: {
        width: "1px",
        alignSelf: "stretch",
        margin: ".25rem 0",
        background: theme.palette.tailwind.slate[200],
    },
    actionButton: {
        width: "3rem",
        height: "3rem",
        borderRadius: ".75rem",
        color: theme.palette.tailwind.slate[700],
        "& svg": {
            fontSize: "1.125rem",
        },
    },
    menuItemDelete: {
        color: theme.palette.error.main,
    },
    statusButtonWrap: {
        display: "inline-flex",
        "& .MuiButton-root": {
            height: "3rem",
            padding: ".875rem 1rem",
            borderRadius: ".75rem",
            fontSize: ".875rem",
        },
        "& .MuiTypography-root": {
            fontSize: ".875rem",
        },
    },
    lockButton: {
        width: "3rem",
        height: "3rem",
        borderRadius: ".75rem",
        "& svg": {
            fontSize: "1.125rem",
        },
    },
    lockedIcon: {
        color: theme.palette.tailwind.slate[700],
    },
    unlockedIcon: {
        color: theme.palette.tailwind.green[500],
    },
}))

type Props = Readonly<{
    order: MeetingServiceOrder
    isSelected: boolean
    onToggleSelect: () => void
    view: MeetingServiceOrdersView
    meetingServiceDependencies: MeetingServiceOrdersDependencies
    timeToStart: TimeToStart | undefined
    onOpenChat: () => void
    onShowReceipt: () => void
    onShowPackingSlip: () => void
    onFullEdit: () => void
    onDelete: () => void
}>

export function ProductionOrderCardHeader({
    order,
    isSelected,
    onToggleSelect,
    view,
    meetingServiceDependencies,
    timeToStart,
    onOpenChat,
    onShowReceipt,
    onShowPackingSlip,
    onFullEdit,
    onDelete,
}: Props) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    // Statuses offered in the dropdown depend on the current view — e.g. the "Not approved" tab (NOT_APPROVED view)
    // offers "Approved", mirroring the regular meeting-service page.
    const hiddenStatuses = getHiddenOrderStatuses(view)

    const [statusAnchor, setStatusAnchor] = useState<{ top: number; left: number } | null>(null)
    const [printMenuAnchor, setPrintMenuAnchor] = useState<HTMLElement | null>(null)
    const [moreMenuAnchor, setMoreMenuAnchor] = useState<HTMLElement | null>(null)

    const invalidateOrders = async ({ queryClient }: { queryClient: QueryClient }) => {
        await queryClient.invalidateQueries({
            queryKey: [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE],
        })
    }

    const { mutate: updateLock } = useApiMutation(changeLockStatusMerchant, {
        onSuccess: (_params, _data, context) => invalidateOrders(context),
    })

    // Changing status opens the same confirmation modal used on the production/meeting-service page.
    const [pendingStatus, setPendingStatus] = useState<FilteredStatuses | null>(null)

    const allowedOrderIds = useMemo(() => {
        if (!pendingStatus) return []
        const transitions = order.orderAction.statusTransitions
        const allowed = transitions?.allowed?.[pendingStatus] || !transitions?.disallowed?.[pendingStatus]
        return allowed ? [order.id] : []
    }, [pendingStatus, order])

    const printMenuItems: ProductionCardMenuItem[] = [
        {
            id: "meeting-service-table-production-slip-button",
            icon: faList,
            fallback: "Packing slip",
            onClick: onShowPackingSlip,
        },
        {
            id: "user-home-canteen-upcoming-orders-receipt-item",
            icon: faReceipt,
            fallback: "Receipt",
            onClick: onShowReceipt,
        },
    ]

    const moreMenuItems: ProductionCardMenuItem[] = [
        {
            id: "meeting-service-production-view-card-full-edit",
            icon: faPenToSquare,
            fallback: "Full edit",
            onClick: onFullEdit,
        },
        {
            id: "order-chat-menu-item-merchant",
            icon: faComment,
            fallback: "Chat with customer",
            onClick: onOpenChat,
        },
        {
            id: "meeting-service-production-view-card-delete-button",
            icon: faTrash,
            fallback: "Delete",
            onClick: onDelete,
            isDelete: true,
        },
    ]

    const { deliveryDetails } = order
    const isLocked = !!deliveryDetails.isLocked

    const handleStatusClick = (event: MouseEvent<HTMLElement>) => {
        const rect = event.currentTarget.getBoundingClientRect()
        setStatusAnchor((prev) => (prev ? null : { top: rect.bottom, left: rect.right }))
    }

    const handleStatusChange = (status: FilteredStatuses) => setPendingStatus(status)

    const handleToggleLock = () => updateLock({ updateLockStatus: { isLocked: !isLocked, orderIds: [order.id] } })

    const lockTooltipId = isLocked
        ? "meeting-service-table-lock-column-lock-tooltip"
        : "meeting-service-table-lock-column-unlock-tooltip"

    return (
        <>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing=".625rem"
                className={classes.headerRow}
            >
                <Stack direction="row" alignItems="center" spacing=".5rem" className={classes.headerLeft}>
                    <Checkbox
                        checked={isSelected}
                        onChange={onToggleSelect}
                        size="small"
                        className={classes.selectCheckbox}
                    />
                    <Stack direction="row" alignItems="center" spacing="1.5rem" className={classes.titleBlock}>
                        <ProductionClampedName
                            name={order.meetingDetails?.meetingName ?? ""}
                            dialogTitleId="meeting-service-production-view-meeting-name-dialog-title"
                            titleClassName={classes.title}
                            noGrow
                        />
                        <ProductionOrderCardMetaRow order={order} showSittings={false} />
                        {timeToStart && (
                            <MeetingTimeLeftTag variant={timeToStart.variant}>
                                <LocalizedStrict
                                    id="meeting-service-production-view-time-left"
                                    vars={{ minutes: timeToStart.minutes }}
                                >
                                    <Typography component="span">{timeToStart.minutes} min left</Typography>
                                </LocalizedStrict>
                            </MeetingTimeLeftTag>
                        )}
                    </Stack>
                </Stack>
                <Stack direction="row" alignItems="center" spacing="1.5rem" className={classes.headerActions}>
                    <OrderChatIcon messages={order.messages} onClick={onOpenChat} className={classes.actionButton} />
                    <LocalizedStrict id="meeting-service-production-view-print-menu-tooltip" attrs={{ title: true }}>
                        <Tooltip arrow title="Print">
                            <IconButton
                                size="small"
                                className={classes.actionButton}
                                aria-label={l10n.getString("meeting-service-production-view-print-menu")}
                                onClick={(event) => setPrintMenuAnchor(event.currentTarget)}
                            >
                                <FontAwesomeIcon icon={faReceipt} />
                            </IconButton>
                        </Tooltip>
                    </LocalizedStrict>
                    <Box className={classes.actionsDivider} />
                    <Box className={classes.statusButtonWrap}>
                        <MinimizedStatusButton
                            status={deliveryDetails.statusDetails.status}
                            tooltipLabel={deliveryDetails.statusDetails.message}
                            onClick={handleStatusClick}
                        />
                    </Box>
                    <StatusButton
                        currentStatus={deliveryDetails.statusDetails.status}
                        hiddenStatuses={hiddenStatuses}
                        anchorEl={statusAnchor}
                        onStatusChange={handleStatusChange}
                        handleClose={() => setStatusAnchor(null)}
                    />
                    <LocalizedStrict id={lockTooltipId} attrs={{ title: true }}>
                        <Tooltip arrow title={l10n.getString(lockTooltipId)}>
                            <IconButton size="small" className={classes.lockButton} onClick={handleToggleLock}>
                                <FontAwesomeIcon
                                    icon={isLocked ? faLock : faLockOpen}
                                    className={isLocked ? classes.lockedIcon : classes.unlockedIcon}
                                />
                            </IconButton>
                        </Tooltip>
                    </LocalizedStrict>
                    <Box className={classes.actionsDivider} />
                    <LocalizedStrict
                        id="meeting-service-production-view-card-menu-button"
                        attrs={{ "aria-label": true }}
                    >
                        <IconButton
                            size="small"
                            className={classes.actionButton}
                            aria-label="More"
                            onClick={(event) => setMoreMenuAnchor(event.currentTarget)}
                        >
                            <FontAwesomeIcon icon={faEllipsisV} />
                        </IconButton>
                    </LocalizedStrict>
                </Stack>
            </Stack>

            <ProductionCardMenu
                anchorEl={printMenuAnchor}
                items={printMenuItems}
                onClose={() => setPrintMenuAnchor(null)}
            />
            <ProductionCardMenu
                anchorEl={moreMenuAnchor}
                items={moreMenuItems}
                deleteClassName={classes.menuItemDelete}
                onClose={() => setMoreMenuAnchor(null)}
            />

            {pendingStatus && (
                <LocalizedStrict id="meeting-service-batch-modal-title" attrs={{ dialogTitle: true }}>
                    <StandardDialog open onClose={() => setPendingStatus(null)} dialogTitle="" maxWidthProp="lg">
                        <StatusChangeModal
                            setShowUndo={() => {}}
                            onClose={() => setPendingStatus(null)}
                            selectedStatus={pendingStatus}
                            allowedOrderIds={allowedOrderIds}
                            orders={[order]}
                            meetingServiceDependencies={meetingServiceDependencies}
                        />
                    </StandardDialog>
                </LocalizedStrict>
            )}
        </>
    )
}
